import { Button, Modal } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { type SpaceAccessImpactResponse, type SpaceResourceResponse, spaceAccessImpactOptions } from "@/api";
import { Text } from "@/components/Text";
import { formatList } from "@/i18n/format";
import { nameOf } from "./useSpaceItems";

const MAX_NAMES = 5;

/**
 * Share an item with the space, or give the space access to it (`grant`), after showing
 * who in the space can't use it and why. Only its admins can give the space access;
 * anyone else can still share it, and members without access see it with a note.
 */
export function ShareItemModal({
  spaceUuid,
  item,
  mode,
  onShare,
  onGrant,
  onClose,
}: Readonly<{
  spaceUuid: string;
  item: SpaceResourceResponse;
  mode: "share" | "grant";
  onShare: () => void;
  onGrant: () => void;
  onClose: () => void;
}>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.items" });
  const impact = useQuery({
    ...spaceAccessImpactOptions({ path: { space_uuid: spaceUuid, resource_uuid: item.uuid } }),
    select: (r) => r.data,
  });
  const name = nameOf(item);
  const someoneCant = (impact.data?.withoutAccess ?? 0) > 0;
  const offerGrant = item.canGrant && (mode === "grant" || someoneCant);

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === "grant" ? t("access.grantTitle", { name }) : t("shareTitle", { name })}
      actions={
        <>
          {/* Three buttons don't fit the dialog; with both share choices, the ✕ / Escape cancel. */}
          {!(mode === "share" && offerGrant) && (
            <Button variant="secondary" onClick={onClose}>
              {t("cancel")}
            </Button>
          )}
          {mode === "share" && (
            <Button variant={offerGrant ? "secondary" : "primary"} onClick={onShare}>
              {offerGrant ? t("access.shareWithout") : t("shareConfirm")}
            </Button>
          )}
          {offerGrant && (
            <Button variant="primary" onClick={onGrant}>
              {t("access.give")}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {impact.isPending && (
          <Text variant="body-sm" tone="secondary">
            {t("access.loading")}
          </Text>
        )}
        {impact.isError && (
          <Text variant="body-sm" tone="secondary">
            {t("access.failed")} {t("shareBody")}
          </Text>
        )}
        {impact.data && <ImpactSummary impact={impact.data} canGrant={item.canGrant ?? false} />}
      </div>
    </Modal>
  );
}

function ImpactSummary({ impact, canGrant }: Readonly<{ impact: SpaceAccessImpactResponse; canGrant: boolean }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.items.access" });
  const people = impact.people ?? [];
  const cant = people.filter((p) => !p.hasAccess).map((p) => p.name);
  const missing = people.filter((p) => (p.missing ?? []).length > 0);
  const shown = cant.slice(0, MAX_NAMES);
  const hidden = impact.withoutAccess - shown.length;
  const names = hidden > 0 ? t("andMore", { names: formatList(shown), count: hidden }) : formatList(shown);

  return (
    <>
      {impact.withoutAccess === 0 ? (
        <Text variant="body-sm">{t("everyoneCan")}</Text>
      ) : (
        <>
          <Text variant="body-sm">
            {t("cantUse", { count: impact.withoutAccess, total: impact.totalMembers, names })}
          </Text>
          <Text variant="body-sm" tone="secondary">
            {canGrant ? t("giveHint") : t("cantGiveHint")}
          </Text>
        </>
      )}
      {missing.length > 0 && (
        <div className="flex flex-col gap-1">
          {missing.slice(0, MAX_NAMES).map((p) => (
            <Text key={p.uuid} variant="body-sm" tone="warning">
              {t("missing", { name: p.name, capabilities: formatList(p.missing ?? []) })}
            </Text>
          ))}
          <Text variant="caption" tone="secondary">
            {t("missingHint")}
          </Text>
        </div>
      )}
    </>
  );
}
